import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Nouria.css';

/* Set once the App Store listing is live. Until then the badge is omitted
   rather than pointing at a page that does not exist yet. */
const APP_STORE_URL = null;

const SCREENSHOTS = [
  { src: '/assets/nouria/01-today.png', title: 'Today', caption: 'Readings, supplements to take, and how you are doing.' },
  { src: '/assets/nouria/02-results.png', title: 'Results', caption: 'Every test from an imported letter, out-of-range flagged.' },
  { src: '/assets/nouria/03-chart.png', title: 'Over time', caption: "One test over time, with the lab's reference range behind the line." },
  { src: '/assets/nouria/04-supplements.png', title: 'Supplements', caption: 'What you take, read off the back of the bottle.' },
  { src: '/assets/nouria/05-assistant.png', title: 'Assistant', caption: 'The assistant, and the questions it suggests.' },
];

const STEPS = [
  {
    Icon: CameraIcon,
    title: 'Photograph a label',
    body: 'Point the camera at the back of a supplement bottle. Nouria reads every ingredient and its dose.',
  },
  {
    Icon: DocumentIcon,
    title: 'Import a test letter',
    body: "Photograph or import a blood test letter. Each result lands on its own chart with the lab's reference range behind it.",
  },
  {
    Icon: ChatIcon,
    title: 'Ask what it means',
    body: 'What does this result mean? Do my supplements overlap? What might be worth raising with my GP?',
  },
];

export default function Nouria() {
  useEffect(() => {
    document.title = 'Nouria — Your results, explained';
    return () => { document.title = 'The Pantry Life'; };
  }, []);

  return (
    <>
      <section className="nouria-section nouria-hero">
        <div className="nouria-container">
          <div className="nouria-hero-inner">
            <img
              src="/assets/nouria/icon-512.png"
              alt=""
              width="96"
              height="96"
              className="nouria-hero-icon"
            />
            <div className="nouria-hero-text">
              <span className="nouria-overline">iOS app · Health</span>
              <h1 className="nouria-display">Nouria</h1>
              <p className="nouria-hero-subtitle">Your results, explained</p>
              <p className="nouria-hero-lead">
                Photograph a supplement label or a blood test letter and Nouria reads it,
                charts it, and explains what it means.
              </p>
              <div className="nouria-hero-actions">
                {APP_STORE_URL ? (
                  <a
                    href={APP_STORE_URL}
                    className="nouria-btn nouria-btn--primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download on the App Store
                  </a>
                ) : (
                  <span className="nouria-pill">Coming soon to the App Store for iPhone</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nouria-section" aria-labelledby="nouria-screens-heading">
        <div className="nouria-container">
          <span className="nouria-overline">In the app</span>
          <h2 id="nouria-screens-heading" className="nouria-section-title">Five screens, one place.</h2>
          <div className="nouria-gallery" role="list">
          {SCREENSHOTS.map((shot, i) => (
            <figure className="nouria-shot" role="listitem" key={shot.src}>
              <div className="nouria-shot-frame">
                <img
                  src={shot.src}
                  alt={`Nouria ${shot.title} screen`}
                  width="1320"
                  height="2868"
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              </div>
              <figcaption>
                <span className="nouria-shot-title">{shot.title}</span>
                <span className="nouria-shot-caption">{shot.caption}</span>
              </figcaption>
            </figure>
          ))}
          </div>
        </div>
      </section>

      <section className="nouria-section" aria-labelledby="nouria-how-heading">
        <div className="nouria-container">
          <span className="nouria-overline">How it works</span>
          <h2 id="nouria-how-heading" className="nouria-section-title">Photograph, chart, ask.</h2>
          <div className="nouria-steps">
            {STEPS.map(({ Icon, title, body }) => (
              <article className="nouria-card nouria-step" key={title}>
                <Icon />
                <h3>{title}</h3>
                <p className="nouria-secondary">{body}</p>
              </article>
            ))}
          </div>
          <p className="nouria-how-note nouria-secondary">
            Your Health app readings sit beside your results, so heart rate, sleep, weight and
            the rest are on the same page as the tests they relate to.
          </p>
        </div>
      </section>

      <section className="nouria-section" aria-labelledby="nouria-privacy-heading">
        <div className="nouria-container">
          <div className="nouria-card nouria-card--mint nouria-privacy-callout">
            <div>
              <span className="nouria-overline">Privacy</span>
              <h2 id="nouria-privacy-heading">Everything stays on your phone.</h2>
              <p>
                Nouria has no accounts and collects nothing. The only thing that ever leaves
                your phone is the question you ask, with a short summary of the records it
                needs to answer it.
              </p>
            </div>
            <Link to="/nouria/privacy" className="nouria-btn nouria-btn--secondary">
              Read the privacy policy
            </Link>
          </div>
        </div>
      </section>

      <section className="nouria-section" aria-labelledby="nouria-pricing-heading">
        <div className="nouria-container">
          <span className="nouria-overline">Pricing</span>
          <h2 id="nouria-pricing-heading" className="nouria-section-title">Free to try.</h2>
          <div className="nouria-plans">
            <article className="nouria-card nouria-plan">
              <h3>Free</h3>
              <p className="nouria-plan-price"><span className="nouria-num">5</span> AI calls</p>
              <p className="nouria-secondary">
                Read labels, import letters and ask the assistant, five times, with nothing
                to sign up for.
              </p>
            </article>
            <article className="nouria-card nouria-plan">
              <h3>Nouria Plus</h3>
              <p className="nouria-plan-price">
                <span className="nouria-num">£5.99</span> a month
              </p>
              <p className="nouria-secondary">
                Fifty AI calls a month, billed through Apple. There is no annual plan, and
                you can cancel any time in your iPhone's Subscriptions settings.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="nouria-section">
        <div className="nouria-container">
          <div className="nouria-card nouria-card--surface nouria-disclaimer">
            <h3>Not medical advice</h3>
            <p className="nouria-secondary">
              Nouria is general information, not medical advice. It does not diagnose and never
              advises on medication changes. Always speak to a qualified clinician about your
              health. If you need help now, call 999 or NHS 111.
            </p>
            <p className="nouria-body-small">
              Questions? <Link to="/nouria/support">Visit support</Link>.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

/* Line icons in secondary ink — no green washes. */
function CameraIcon() {
  return (
    <svg className="nouria-step-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8.5A1.5 1.5 0 015.5 7h2.2l1.1-1.6A1 1 0 019.6 5h4.8a1 1 0 01.8.4L16.3 7h2.2A1.5 1.5 0 0120 8.5v8A1.5 1.5 0 0118.5 18h-13A1.5 1.5 0 014 16.5v-8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg className="nouria-step-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 3.5h6.5L18 8v11a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 016 19V5a1.5 1.5 0 011-1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M13.5 3.5V8H18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 12h6M9 15.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="nouria-step-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 6.5A1.5 1.5 0 016 5h12a1.5 1.5 0 011.5 1.5v8A1.5 1.5 0 0118 16h-7.5l-4 3.5V16H6a1.5 1.5 0 01-1.5-1.5v-8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.5 9.5h7M8.5 12.5h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
